/**
 * RBAC Management Global Tool (FH-RBAC-TOOL-WIRE / PRE-162)
 *
 * Wraps the existing server-side RBAC callable functions (rbacGetMyPermissions,
 * rbacListMembers, rbacListRoles, rbacAssignRoles, rbacRevokeRole,
 * rbacCreateRole, rbacUpdateRole, rbacDeleteRole) so the chat orchestrator
 * can introspect and mutate team/role/permission state in natural language.
 *
 * The original MCP definition in src/mcp/tools/rbacManagementTool.ts was never
 * connected to the GlobalTools runtime; this file bridges that gap by reusing
 * the existing frontend wrappers in src/services/rbacService.ts (which already
 * call httpsCallable('rbacXxx', ...)). All permission enforcement is handled
 * server-side — this wrapper adds zero trust surface.
 */
import {
  getMyPermissions,
  listMembers,
  listRoles,
  assignRoles,
  revokeRole,
  createRole,
  updateRole,
  deleteRole,
} from '../../rbacService';
import { isAgenticWorkflowsAuthorized } from '../../agenticWorkflows';
import { makeRegexTrigger } from '../triggerHelpers';

import type {
  GlobalToolDefinition,
  StepContext,
  StepResult,
  ToolTheme,
} from '../types';

// ============================================================================
// Types
// ============================================================================

type RbacOperation =
  | 'get_my_permissions'
  | 'list_members'
  | 'list_roles'
  | 'assign_role'
  | 'assign_roles'
  | 'revoke_role'
  | 'create_role'
  | 'update_role'
  | 'delete_role';

interface RbacInput {
  operation?: RbacOperation;
  // operator-supplied fields (snake_case from MCP schema, camelCase fallback)
  user_id?: string;
  userId?: string;
  role_id?: string;
  roleId?: string;
  role_ids?: string[];
  roleIds?: string[];
  role_name?: string;
  name?: string;
  role_description?: string;
  description?: string;
  grants?: string[];
  inherits_from?: string[];
  inheritsFrom?: string[];
  replacement_role_id?: string;
  replacementRoleId?: string;
  query?: string;
}

export interface RbacManagementState {
  isRunning: boolean;
  lastOperation: RbacOperation | null;
  lastResult: unknown;
}

// ============================================================================
// Theme
// ============================================================================

const RBAC_THEME: ToolTheme = {
  accent: '#7c3aed',    // Violet 600
  accentBg: '#ede9fe',  // Violet 100
  border: '#8b5cf6',    // Violet 500
  subtitle: '#5b21b6',  // Violet 900
  muted: '#a78bfa',     // Violet 400
};

// ============================================================================
// Trigger pattern — route permissions/roles/team questions here
// ============================================================================
//
// Phase 0 hardening (Slack C0AMX0ACS9K/1778451390.755569):
//   • Pattern 0: dropped overloaded `access` keyword. "what tools you have
//     access to" must NOT route to RBAC (today's bug).
//   • Pattern 1: dropped `access` from the noun half — "do i have access"
//     remains valid via the role/permission/right/ability anchors only.
//   • Pattern 2: dropped `access` from "my X" — `my access` is too generic.
//   • Window quantifiers: `[^?]{0,N}` (N up to 60) replaced with
//     `[^.;\n]{0,30}` so triggers stay scoped to a single clause and can't
//     span across sentence boundaries.
//   • All matches are run through `makeRegexTrigger` from triggerHelpers
//     which applies the in-clause negation guard ("I'm not talking about
//     roles and permissions" → no match).

const RBAC_TRIGGER_PATTERNS: RegExp[] = [
  // Pattern 0: REQUIRED change — `access` removed (it's overloaded;
  //            "what tools you have access to" must NOT route to RBAC).
  //            Window narrowed from `[^?]{0,60}` to `[^.;\n]{0,30}`.
  /\b(what|which|list|show|tell me|see)\b[^.;\n]{0,30}\b(my\s+)?(permissions?|grants?|privileges?)\b/i,
  // Pattern 1: `access` retained — `(permission|access|the right|the ability)`
  //            is a domain-anchored alternation, not a generic "access" check.
  //            Window narrowed from `[^?]{0,60}` to `[^.;\n]{0,30}`.
  /\b(do i have|can i)\b[^.;\n]{0,30}\b(permission|access|the right|the ability)\b/i,
  // Pattern 2: `access` retained — alternation with permissions/roles
  //            keeps it domain-anchored.
  /\bmy (permissions?|roles?|access)\b/i,
  /\b(who|which|list|show)\b[^.;\n]{0,30}\b(team\s+)?members?\b/i,
  /\b(list|show|what are)\b[^.;\n]{0,30}\broles?\b/i,
  /\b(assign|give|grant)\b[^.;\n]{0,30}\brole\b/i,
  /\b(revoke|remove|take away|unassign)\b[^.;\n]{0,30}\brole\b/i,
  /\b(create|make|add)\b[^.;\n]{0,30}\b(new\s+)?role\b/i,
  /\b(update|edit|modify|change)\b[^.;\n]{0,30}\brole\b/i,
  /\b(delete|remove)\b[^.;\n]{0,30}\brole\b/i,
];

const matchRbacTrigger = makeRegexTrigger(RBAC_TRIGGER_PATTERNS);

// ============================================================================
// Helpers
// ============================================================================

function inferOperation(text: string): RbacOperation {
  const t = text.toLowerCase();
  if (
    /\b(my|i\s+have|do\s+i)\b.*\b(permission|access|grant|role)/.test(t) ||
    (/\b(permission|access|grant)s?\b/.test(t) && /\bmy\b/.test(t))
  ) {
    return 'get_my_permissions';
  }
  if (/\bmember/.test(t) && !/\brole\b.*\bassign/.test(t)) return 'list_members';
  if (/\b(assign|give|grant)\b.*\brole/.test(t)) return 'assign_role';
  if (/\b(revoke|remove|take away|unassign)\b.*\brole/.test(t)) return 'revoke_role';
  if (/\b(create|make|add)\b.*\brole/.test(t)) return 'create_role';
  if (/\b(update|edit|modify|change)\b.*\brole/.test(t)) return 'update_role';
  if (/\b(delete|remove)\b.*\brole/.test(t)) return 'delete_role';
  if (/\broles?\b/.test(t)) return 'list_roles';
  // Default: permissions introspection (Miles gap #2 primary use case).
  return 'get_my_permissions';
}

// ============================================================================
// Natural-language parameter extraction (FH-BUG-002 fix, 2026-05-01)
//
// inferOperation() extracts the VERB (create/update/delete/etc) from the user's
// trigger text, but the mutation branches (create_role / update_role /
// delete_role / assign_role / revoke_role) also need structured parameters
// (role_name, grants, role_id, user_id). When the orchestrator routes the
// message directly via matchToolTrigger (the direct-pattern path), it passes
// only `{ query: trimmed }` — no extracted params — which caused every
// mutation to throw "<op> requires <field>" even when the user's text clearly
// named the role. parseRbacPrompt() closes that gap by pulling the common
// shapes out of natural language, with explicit structured prompts taking
// precedence (see parseRbacPrompt docstring).
// ============================================================================

interface ParsedRbacPrompt {
  role_name?: string;
  grants?: string[];
  role_id?: string;
  user_id?: string;
  role_ids?: string[];
  replacement_role_id?: string;
  role_description?: string;
}

const PERMISSION_TOKEN_RE = /\b((?:custom\.)?[a-z][a-z0-9_-]*(?:\.[a-z][a-z0-9_*-]*)+)\b/g;
const UID_LIKE_RE = /\b(uid[_-]?[A-Za-z0-9]{4,}|[A-Za-z0-9]{20,})\b/;
const EMAIL_RE = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/;

function parseRbacPrompt(raw: string): ParsedRbacPrompt {
  const out: ParsedRbacPrompt = {};
  if (!raw) return out;
  const text = raw.trim();

  // ── Explicit structured "role_name is \"foo\"" / role_name="foo" ────────
  const structuredName =
    text.match(/\brole[_\s-]?name\s*(?:is|=|:)\s*["'`]([^"'`]+)["'`]/i) ||
    text.match(/\brole[_\s-]?name\s*(?:is|=|:)\s*([A-Za-z0-9][\w.-]{0,80})/i);
  if (structuredName) out.role_name = structuredName[1].trim();

  // ── Natural-language role-name extraction ──────────────────────────────
  if (!out.role_name) {
    // "role called foo" / "role named foo" / "called \"foo\"" / "named foo"
    const quotedNamed = text.match(/\b(?:called|named)\s+["'`]([^"'`]+)["'`]/i);
    if (quotedNamed) out.role_name = quotedNamed[1].trim();
    else {
      const naturalNamed = text.match(
        /\b(?:role\s+(?:called|named)|called|named)\s+([A-Za-z0-9][\w.-]{0,60})/i,
      );
      if (naturalNamed) out.role_name = naturalNamed[1].trim();
    }
  }

  // Strip trailing punctuation/words that commonly attach to the name
  // (e.g. "test-temp." or "test-temp with" → "test-temp")
  if (out.role_name) {
    out.role_name = out.role_name.replace(/[.,;:!?]+$/, '').trim();
  }

  // ── Strip text inside emails so email domains aren't parsed as permissions ──
  // (e.g. "assign role to jane@example.com" shouldn't yield grants=['example.com'])
  const textMinusEmails = text.replace(EMAIL_RE, ' ');

  // ── Permissions / grants extraction ────────────────────────────────────
  // Explicit structured: permissions is ["foo.bar", "baz.qux"] or
  // grants=["foo.bar"] or permissions: foo.bar, baz.qux
  // NOTE: we do NOT use `[^.;\n]` as terminator because permission keys
  // themselves contain dots (e.g. `documents.view`). Terminate on semicolon,
  // newline, or trailing sentence end ("." followed by whitespace/end).
  const structuredGrants =
    textMinusEmails.match(/\b(?:permissions?|grants?)\s*(?:is|=|:)\s*\[([^\]]+)\]/i) ||
    textMinusEmails.match(/\b(?:permissions?|grants?)\s*(?:is|=|:)\s*([^;\n]+?)(?:[.!?](?:\s|$)|$)/i);
  if (structuredGrants) {
    const inner = structuredGrants[1];
    const tokens = (inner.match(PERMISSION_TOKEN_RE) || []).map((s) => s.trim());
    if (tokens.length) out.grants = Array.from(new Set(tokens));
  }

  // Natural-language: "with permission foo.bar" / "with permissions foo.bar, baz.qux"
  // Terminate on sentence-end ("." + whitespace/eol) so "permission documents.view."
  // captures the full "documents.view" token.
  if (!out.grants) {
    const withPerms = textMinusEmails.match(
      /\bwith\s+(?:only\s+)?(?:the\s+)?permissions?\s+(.{1,200}?)(?:[.!?](?:\s|$)|$)/i,
    );
    if (withPerms) {
      const tokens = (withPerms[1].match(PERMISSION_TOKEN_RE) || []).map((s) => s.trim());
      if (tokens.length) out.grants = Array.from(new Set(tokens));
    }
  }

  // Fallback: only run when the prompt reads as a create_role / update_role
  // intent (contains "role" AND at least one of the create/update verbs).
  // Without this guard, "assign role Admin to jane@foo.io" would pull foo.io
  // as a fake grant.
  const looksLikeRoleMutation =
    /\b(create|make|add|update|edit|modify|change)\b[^.]{0,40}\brole\b/i.test(
      textMinusEmails,
    );
  if (!out.grants && looksLikeRoleMutation) {
    const allTokens = (textMinusEmails.match(PERMISSION_TOKEN_RE) || []).map((s) => s.trim());
    const filtered = allTokens.filter((t) =>
      /^(custom\.)?[a-z][a-z0-9_-]*\.[a-z][a-z0-9_*-]*(\.[a-z][a-z0-9_*-]*)?$/.test(t),
    );
    if (filtered.length) out.grants = Array.from(new Set(filtered));
  }

  // ── role_id extraction ────────────────────────────────────────────────
  const structuredRoleId =
    text.match(/\brole[_\s-]?id\s*(?:is|=|:)\s*["'`]([^"'`]+)["'`]/i) ||
    text.match(/\brole[_\s-]?id\s*(?:is|=|:)\s*([A-Za-z0-9][\w-]{2,80})/i);
  if (structuredRoleId) out.role_id = structuredRoleId[1].trim();

  // Natural-language: "delete role role_xyz" / "update role foo_abc123"
  // Only match tokens that look like generated role ids (underscore or long).
  if (!out.role_id) {
    const naturalRoleIdMatch = text.match(
      /\b(?:delete|update|edit|remove|modify)\b[^.]{0,30}\brole\s+(role[_-][A-Za-z0-9_-]{3,}|[A-Za-z0-9]{16,})\b/i,
    );
    if (naturalRoleIdMatch) out.role_id = naturalRoleIdMatch[1].trim();
  }

  // ── user_id extraction (email preferred, then UID) ─────────────────────
  const structuredUser =
    text.match(/\buser[_\s-]?id\s*(?:is|=|:)\s*["'`]([^"'`]+)["'`]/i) ||
    text.match(/\buser[_\s-]?id\s*(?:is|=|:)\s*([A-Za-z0-9][\w.@+-]{2,120})/i);
  if (structuredUser) {
    out.user_id = structuredUser[1].trim();
  } else {
    const email = text.match(EMAIL_RE);
    if (email) out.user_id = email[0];
    else {
      const uid = text.match(UID_LIKE_RE);
      if (uid && !/^[0-9]+$/.test(uid[0])) out.user_id = uid[0];
    }
  }

  // ── role_description extraction ────────────────────────────────────────
  const structuredDesc =
    text.match(/\b(?:role[_\s-]?)?description\s*(?:is|=|:)\s*["'`]([^"'`]+)["'`]/i);
  if (structuredDesc) out.role_description = structuredDesc[1].trim();

  return out;
}

/**
 * Merge natural-language-extracted fields into the structured input,
 * without overwriting any explicitly-provided values. Explicit structured
 * input from the orchestrator/LLM always wins; parseRbacPrompt only fills
 * in the gaps when the tool is invoked via the direct trigger-pattern path.
 */
function mergeRbacInput(structured: RbacInput, parsed: ParsedRbacPrompt): RbacInput {
  return {
    ...structured,
    role_name: structured.role_name || structured.name || parsed.role_name,
    role_id: structured.role_id || structured.roleId || parsed.role_id,
    user_id: structured.user_id || structured.userId || parsed.user_id,
    grants: structured.grants && structured.grants.length ? structured.grants : parsed.grants,
    role_description:
      structured.role_description || structured.description || parsed.role_description,
  };
}

// Exported for unit testing.
export const __rbacInternals = { parseRbacPrompt, mergeRbacInput, inferOperation };

/**
 * Resolve a role_id from either an explicit id OR a role name via listRoles().
 * Returns null if neither is available or no match is found.
 *
 * This lets users say "delete the role test-temp" and have the tool find the
 * corresponding role id rather than bailing out with "delete_role requires
 * role_id". System roles (owner, admin, etc.) can still be addressed by name.
 */
async function resolveRoleId(
  explicitRoleId: string | undefined,
  roleName: string | undefined,
): Promise<string | null> {
  if (explicitRoleId) return explicitRoleId;
  if (!roleName) return null;
  try {
    const data = await listRoles();
    const roles = Array.isArray(data?.roles) ? data.roles : [];
    const needle = roleName.trim().toLowerCase();
    // Prefer exact (case-insensitive) name match, then id match.
    const byName = roles.find(
      (r: any) => typeof r?.name === 'string' && r.name.trim().toLowerCase() === needle,
    );
    if (byName && typeof byName.id === 'string') return byName.id;
    const byId = roles.find(
      (r: any) => typeof r?.id === 'string' && r.id.toLowerCase() === needle,
    );
    if (byId && typeof byId.id === 'string') return byId.id;
    // Loose substring fallback — only if unique to avoid ambiguous matches.
    const loose = roles.filter(
      (r: any) => typeof r?.name === 'string' && r.name.toLowerCase().includes(needle),
    );
    if (loose.length === 1 && typeof loose[0].id === 'string') return loose[0].id;
    return null;
  } catch (err) {
    console.warn('[rbac-management] resolveRoleId: listRoles failed', err);
    return null;
  }
}

function renderPermissionsHtml(permissions: string[], isOwner: boolean): string {
  const customPerms = permissions.filter((p) => p.startsWith('custom.'));
  const standardPerms = permissions.filter((p) => !p.startsWith('custom.'));

  const ownerBanner = isOwner
    ? `<div style="display:inline-flex;align-items:center;gap:6px;padding:4px 10px;background:#fef3c7;border:1px solid #fde68a;border-radius:999px;color:#92400e;font-size:12px;font-weight:600;margin-bottom:10px;">
         <span>👑</span><span>account owner — full access</span>
       </div>`
    : '';

  const customSection = customPerms.length
    ? `<div style="margin:12px 0 10px;padding:10px 12px;background:#f5f3ff;border:1px solid #ddd6fe;border-radius:8px;">
         <div style="font-size:12px;font-weight:600;color:#5b21b6;margin-bottom:6px;">custom permissions (firm-scoped)</div>
         <ul style="margin:0;padding-left:18px;color:#4c1d95;font-size:13px;line-height:1.6;">
           ${customPerms.map((p) => `<li><code style="background:#ede9fe;padding:1px 6px;border-radius:4px;">${escapeHtml(p)}</code></li>`).join('')}
         </ul>
       </div>`
    : '';

  const standardSection = standardPerms.length
    ? `<div style="margin-top:10px;">
         <div style="font-size:12px;font-weight:600;color:#475569;margin-bottom:6px;">standard permissions</div>
         <ul style="margin:0;padding-left:18px;color:#334155;font-size:13px;line-height:1.6;">
           ${standardPerms.map((p) => `<li><code style="background:#f1f5f9;padding:1px 6px;border-radius:4px;">${escapeHtml(p)}</code></li>`).join('')}
         </ul>
       </div>`
    : (isOwner && !customPerms.length
        ? `<div style="color:#475569;font-size:13px;margin-top:8px;">as owner you implicitly hold every permission in this account.</div>`
        : (!customPerms.length
            ? `<div style="color:#64748b;font-size:13px;margin-top:8px;">no effective permissions assigned. ask an admin to grant you a role.</div>`
            : ''));

  return `
    <div style="padding:14px 16px;background:#faf5ff;border:1px solid #e9d5ff;border-radius:12px;font-family:'SF Pro Text',-apple-system,BlinkMacSystemFont,sans-serif;">
      <div style="font-weight:600;color:#5b21b6;font-size:14px;margin-bottom:4px;">your effective permissions</div>
      ${ownerBanner}
      ${customSection}
      ${standardSection}
    </div>
  `;
}

function renderPermissionsText(permissions: string[], isOwner: boolean): string {
  const customPerms = permissions.filter((p) => p.startsWith('custom.'));
  const standardPerms = permissions.filter((p) => !p.startsWith('custom.'));
  const lines: string[] = ['your effective permissions:'];
  if (isOwner) lines.push('• account owner (full access)');
  if (customPerms.length) {
    lines.push('', 'custom permissions (firm-scoped):');
    for (const p of customPerms) lines.push(`  • ${p}`);
  }
  if (standardPerms.length) {
    lines.push('', 'standard permissions:');
    for (const p of standardPerms) lines.push(`  • ${p}`);
  }
  if (!permissions.length && !isOwner) {
    lines.push('(none assigned — ask an admin to grant you a role)');
  }
  return lines.join('\n');
}

function renderSimpleHtml(title: string, body: string): string {
  return `
    <div style="padding:14px 16px;background:#faf5ff;border:1px solid #e9d5ff;border-radius:12px;font-family:'SF Pro Text',-apple-system,BlinkMacSystemFont,sans-serif;">
      <div style="font-weight:600;color:#5b21b6;font-size:14px;margin-bottom:6px;">${escapeHtml(title)}</div>
      <div style="color:#334155;font-size:13px;line-height:1.6;">${body}</div>
    </div>
  `;
}

function escapeHtml(s: string): string {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function formatError(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  try { return JSON.stringify(err); } catch { return 'unknown error'; }
}

// ============================================================================
// Tool Definition
// ============================================================================

export const rbacManagementGlobalTool: GlobalToolDefinition<RbacManagementState> = {
  id: 'rbac-management',
  name: 'Team & Roles Management',
  description:
    'Introspect or mutate team members, roles, and permissions for the current account. ' +
    'Use for questions like "what are my permissions?", "list team members", "what roles exist?", ' +
    'or admin actions like assigning/revoking roles and creating/updating/deleting custom roles. ' +
    'All mutations are permission-gated server-side.',

  triggerPattern: matchRbacTrigger,

  createInitialState: (): RbacManagementState => ({
    isRunning: false,
    lastOperation: null,
    lastResult: null,
  }),

  buttonIds: {},

  steps: [
    {
      id: 'execute-rbac',
      name: 'Execute RBAC Operation',
      activeDisplayName: 'checking permissions',

      execute: async (
        ctx: StepContext<RbacManagementState>,
        rawInput?: unknown,
      ): Promise<StepResult> => {
        const structured = (rawInput || {}) as RbacInput;
        const triggerText = ctx.triggerMessage?.text || '';
        const operation: RbacOperation = (structured.operation as RbacOperation) || inferOperation(triggerText);

        // FH-BUG-002 fix (2026-05-01): fill in role_name / grants / role_id /
        // user_id / description from natural language when the structured
        // input doesn't already have them. This lets phrases like
        // "Create a role called test-temp with permission cases.read" work
        // end-to-end via the direct trigger-pattern path, which otherwise
        // only passes { query, onStreamingStart } to this step.
        const parsed = parseRbacPrompt(triggerText);
        const input: RbacInput = mergeRbacInput(structured, parsed);

        ctx.setState({ isRunning: true, lastOperation: operation });

        const stream = ctx.messaging.showPending(
          operation === 'get_my_permissions' ? 'checking your permissions' :
          operation === 'list_members' ? 'loading team members' :
          operation === 'list_roles' ? 'loading roles' :
          'applying rbac change'
        );

        try {
          switch (operation) {
            case 'get_my_permissions': {
              const data = await getMyPermissions();
              const permissions = Array.isArray(data?.permissions) ? data.permissions : [];
              const isOwner = Boolean(data?.isOwner);

              ctx.setState({ isRunning: false, lastResult: data });

              // 2026-05-10 (Miles directive): emit structured event; the
              // post-tool renderer composes the user-visible reply in the
              // user's voice/language. NO hardcoded text/html here.
              stream.finalize({
                payload: {
                  kind: 'rbac.my_permissions',
                  toolId: 'rbac-management',
                  data: { permissions, isOwner, rawResponse: data },
                  summary: `RBAC get_my_permissions: ${permissions.length} permission${permissions.length === 1 ? '' : 's'}${isOwner ? ' (account owner — full access)' : ''}.`,
                },
                sourceTag: 'rbac_my_permissions',
              });
              return { status: 'completed', output: data };
            }

            case 'list_members': {
              const data = await listMembers();
              const members = Array.isArray(data?.members) ? data.members : [];

              ctx.setState({ isRunning: false, lastResult: data });
              stream.finalize({
                payload: {
                  kind: 'rbac.list_members',
                  toolId: 'rbac-management',
                  data: { members, rawResponse: data },
                  summary: `RBAC list_members: ${members.length} member${members.length === 1 ? '' : 's'}.`,
                },
                sourceTag: 'rbac_list_members',
              });
              return { status: 'completed', output: data };
            }

            case 'list_roles': {
              const data = await listRoles();
              const roles = Array.isArray(data?.roles) ? data.roles : [];
              const systemCount = roles.filter((r: any) => r?.system).length;

              ctx.setState({ isRunning: false, lastResult: data });
              stream.finalize({
                payload: {
                  kind: 'rbac.list_roles',
                  toolId: 'rbac-management',
                  data: { roles, rawResponse: data },
                  summary: `RBAC list_roles: ${roles.length} role${roles.length === 1 ? '' : 's'}${systemCount ? ` (${systemCount} system-defined)` : ''}.`,
                },
                sourceTag: 'rbac_list_roles',
              });
              return { status: 'completed', output: data };
            }

            case 'assign_role':
            case 'assign_roles': {
              const userId = input.user_id || input.userId;
              let roleIds =
                input.role_ids ||
                input.roleIds ||
                (input.role_id ? [input.role_id] : input.roleId ? [input.roleId] : []);
              if (!roleIds.length) {
                // Try resolving role_name → role_id so "assign the Admin role to jane@foo.com" works.
                const resolved = await resolveRoleId(undefined, input.role_name || input.name);
                if (resolved) roleIds = [resolved];
              }
              if (!userId || !roleIds.length) {
                throw new Error('assign_role requires user_id and at least one role_id');
              }
              const data = await assignRoles({ userId, roleIds });
              ctx.setState({ isRunning: false, lastResult: data });
              stream.finalize({
                payload: {
                  kind: 'rbac.assign_role',
                  toolId: 'rbac-management',
                  data: {
                    operation: 'assign_role',
                    userId,
                    roleIds,
                    roleName: input.role_name || input.name,
                    rawResponse: data,
                  },
                  summary: `RBAC assign_role: assigned ${roleIds.join(', ')} to ${userId}.`,
                },
                sourceTag: 'rbac_assign_role',
              });
              return { status: 'completed', output: data };
            }

            case 'revoke_role': {
              const userId = input.user_id || input.userId;
              let roleId = input.role_id || input.roleId;
              if (!roleId) {
                const resolved = await resolveRoleId(undefined, input.role_name || input.name);
                if (resolved) roleId = resolved;
              }
              if (!userId || !roleId) throw new Error('revoke_role requires user_id and role_id');
              const data = await revokeRole(userId, roleId);
              ctx.setState({ isRunning: false, lastResult: data });
              stream.finalize({
                payload: {
                  kind: 'rbac.revoke_role',
                  toolId: 'rbac-management',
                  data: {
                    operation: 'revoke_role',
                    userId,
                    roleId,
                    roleName: input.role_name || input.name,
                    rawResponse: data,
                  },
                  summary: `RBAC revoke_role: revoked ${roleId} from ${userId}.`,
                },
                sourceTag: 'rbac_revoke_role',
              });
              return { status: 'completed', output: data };
            }

            case 'create_role': {
              const name = input.role_name || input.name;
              if (!name) throw new Error('create_role requires role_name');
              // FH-BUG-002 (retry fix, 2026-05-01): httpsCallable serializes
              // undefined → null over the wire, and the CF destructures with
              // `inheritsFrom = []` default — which fires on undefined but NOT
              // on null. Passing null produces `TypeError: inheritsFrom is not
              // iterable` in rbacCreateRole. Always send concrete arrays.
              const data = await createRole({
                name,
                description: input.role_description || input.description,
                grants: Array.isArray(input.grants) ? input.grants : [],
                inheritsFrom: Array.isArray(input.inherits_from)
                  ? input.inherits_from
                  : Array.isArray(input.inheritsFrom)
                  ? input.inheritsFrom
                  : [],
              });
              ctx.setState({ isRunning: false, lastResult: data });
              stream.finalize({
                payload: {
                  kind: 'rbac.create_role',
                  toolId: 'rbac-management',
                  data: {
                    operation: 'create_role',
                    roleName: name,
                    rawResponse: data,
                  },
                  summary: `RBAC create_role: created role "${name}".`,
                },
                sourceTag: 'rbac_create_role',
              });
              return { status: 'completed', output: data };
            }

            case 'update_role': {
              let roleId = input.role_id || input.roleId;
              if (!roleId) {
                const resolved = await resolveRoleId(undefined, input.role_name || input.name);
                if (resolved) roleId = resolved;
              }
              if (!roleId) throw new Error('update_role requires role_id');
              // Same wire-null issue as create_role: only include grants /
              // inheritsFrom when the caller explicitly provided arrays.
              // Fields we omit are "don't touch"; fields we send must be
              // real arrays to avoid "not iterable" on the CF side.
              const grantsForUpdate = Array.isArray(input.grants) ? input.grants : undefined;
              const inheritsForUpdate = Array.isArray(input.inherits_from)
                ? input.inherits_from
                : Array.isArray(input.inheritsFrom)
                ? input.inheritsFrom
                : undefined;
              const data = await updateRole({
                roleId,
                name: input.role_name || input.name,
                description: input.role_description || input.description,
                grants: grantsForUpdate,
                inheritsFrom: inheritsForUpdate,
              });
              ctx.setState({ isRunning: false, lastResult: data });
              stream.finalize({
                payload: {
                  kind: 'rbac.update_role',
                  toolId: 'rbac-management',
                  data: {
                    operation: 'update_role',
                    roleId,
                    roleName: input.role_name || input.name,
                    rawResponse: data,
                  },
                  summary: `RBAC update_role: updated role ${roleId}.`,
                },
                sourceTag: 'rbac_update_role',
              });
              return { status: 'completed', output: data };
            }

            case 'delete_role': {
              let roleId = input.role_id || input.roleId;
              if (!roleId) {
                const resolved = await resolveRoleId(undefined, input.role_name || input.name);
                if (resolved) roleId = resolved;
              }
              if (!roleId) throw new Error('delete_role requires role_id');
              const data = await deleteRole(roleId);
              ctx.setState({ isRunning: false, lastResult: data });
              stream.finalize({
                payload: {
                  kind: 'rbac.delete_role',
                  toolId: 'rbac-management',
                  data: {
                    operation: 'delete_role',
                    roleId,
                    rawResponse: data,
                  },
                  summary: `RBAC delete_role: deleted role ${roleId}.`,
                },
                sourceTag: 'rbac_delete_role',
              });
              return { status: 'completed', output: data };
            }

            default: {
              throw new Error(`unsupported rbac operation: ${operation}`);
            }
          }
        } catch (err) {
          ctx.setState({ isRunning: false });
          const msg = formatError(err);
          stream.finalize({
            payload: {
              kind: 'rbac.error',
              toolId: 'rbac-management',
              severity: 'error',
              data: { operation, errorMessage: msg },
              summary: `RBAC ${operation} failed: ${msg}`,
            },
            sourceTag: 'rbac_error',
          });
          return {
            status: 'failed',
            error: new Error(`rbac ${operation} failed: ${msg}`),
            userFacingErrorShown: true,
          };
        }
      },

      cleanup: async (ctx: StepContext<RbacManagementState>) => {
        ctx.setState({ isRunning: false });
      },
    },
  ],

  isAuthorized: isAgenticWorkflowsAuthorized,

  theme: RBAC_THEME,

  canTriggerFromButton: false,
};

export default rbacManagementGlobalTool;
